import React, { useState, useCallback } from "react";
import {
  Box, Typography, Button, CircularProgress, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, TextField, Divider, Snackbar,
  FormControlLabel, Checkbox, Stack, Paper,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MergeIcon from "@mui/icons-material/MergeType";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useNavigate } from "react-router-dom";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import ZephyrIconWhite from "@/Images/Icons/Zephyr_Icon_White.png";

const Z_PURPLE = "#38184f";
const Z_BLUE   = "#2baede";
const fmtDate  = (d) => d ? new Date(d).toLocaleDateString("en-AU") : "—";

// ── Script Report Dialog ──────────────────────────────────────────────────────
function ScriptReportDialog({ open, claimID, fullName, onClose }) {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!open || !claimID) return;
    setLoading(true);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_FULL_SCRIPT_REPORT}/${claimID}`, "GET")
      .then(res => setScripts(res?.data?.scripts ?? []))
      .finally(() => setLoading(false));
  }, [open, claimID]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5,
      }}>
        <Box>
          <Typography fontWeight={700}>Full Script Report</Typography>
          {fullName && <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>{fullName}</Typography>}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {loading
          ? <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress sx={{ color: Z_PURPLE }} /></Box>
          : (
            <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1 }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: `${Z_PURPLE}08` }}>
                  <TableRow>
                    {["Invoice", "Date", "Item", "Qty", "Invoiced", "Received", "Status"].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 1 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scripts.map(s => (
                    <TableRow key={s.ScriptID} sx={{ "& td": { py: 0.6, px: 1, fontSize: "0.8rem" } }}>
                      <TableCell>{s.InvoiceID ?? "—"}</TableCell>
                      <TableCell>{fmtDate(s.DateDispensed)}</TableCell>
                      <TableCell>{s.txtItem}</TableCell>
                      <TableCell align="center">{s.Qty ?? "—"}</TableCell>
                      <TableCell align="right">{s.InvAmount != null ? `$${Number(s.InvAmount).toFixed(2)}` : "—"}</TableCell>
                      <TableCell align="right">{s.RcvdAmount != null ? `$${Number(s.RcvdAmount).toFixed(2)}` : "—"}</TableCell>
                      <TableCell>
                        {s.PaymentID
                          ? <Chip label="Paid" size="small" sx={{ background: "#dcfce7", color: "#16a34a", fontSize: "0.7rem" }} />
                          : Number(s.WrittenOff)
                            ? <Chip label="Written Off" size="small" sx={{ background: "#fee2e2", color: "#dc2626", fontSize: "0.7rem" }} />
                            : Number(s.OnHold) === -1
                              ? <Chip label="Missed" size="small" sx={{ background: "#fef9c3", color: "#92400e", fontSize: "0.7rem" }} />
                              : <Chip label="Outstanding" size="small" variant="outlined" sx={{ fontSize: "0.7rem" }} />}
                      </TableCell>
                    </TableRow>
                  ))}
                  {scripts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No scripts found.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Customer Result Row ───────────────────────────────────────────────────────
function CustomerResultRow({ row, actionLabel, actionColor = "primary", onAction, onViewScripts }) {
  return (
    <TableRow sx={{ "& td": { py: 0.75, px: 1.5, fontSize: "0.82rem" } }}>
      <TableCell sx={{ fontWeight: 600, color: Z_PURPLE }}>{row.fullName}</TableCell>
      <TableCell>{row.claimNumber ?? "—"}</TableCell>
      <TableCell sx={{ color: "#64748b", fontSize: "0.78rem" }}>
        {row.insCompName ?? row.employerName ?? "—"}
      </TableCell>
      <TableCell align="center">{row.totalScripts}</TableCell>
      <TableCell>{fmtDate(row.lastDispensed)}</TableCell>
      <TableCell sx={{ width: "1px", whiteSpace: "nowrap" }}>
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
          <Tooltip title="View full script list">
            <IconButton size="small" onClick={() => onViewScripts(row)} sx={{ color: "#64748b", p: 0.5 }}>
              <ListAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button size="small" variant="contained" color={actionColor}
            onClick={() => onAction(row)}
            sx={{ textTransform: "none", fontSize: "0.74rem", py: 0.3, px: 1.2, whiteSpace: "nowrap" }}>
            {actionLabel}
          </Button>
        </Box>
      </TableCell>
    </TableRow>
  );
}

// ── Merge Customer Tool ───────────────────────────────────────────────────────
function MergeCustomerTool({ onDone }) {
  const navigate = useNavigate();

  // Step: 'search-keep' | 'select-merge' | 'confirming' | 'done'
  const [step, setStep] = useState('search-keep');

  // Keep customer search
  const [keepQuery, setKeepQuery] = useState('');
  const [keepResults, setKeepResults] = useState(null);
  const [keepLoading, setKeepLoading] = useState(false);
  const [keepCustomer, setKeepCustomer] = useState(null); // full details

  // Merge candidates
  const [candidateFilter, setCandidateFilter] = useState('');
  const [candidates, setCandidates] = useState(null);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [selectedMerge, setSelectedMerge] = useState(new Set()); // Set of customerIDs

  // Merge execution
  const [merging, setMerging] = useState(false);
  const [mergeResult, setMergeResult] = useState(null);

  // Script report dialog
  const [scriptReport, setScriptReport] = useState(null); // { claimID, fullName }

  // ── Keep customer search ──
  const searchKeep = useCallback(async () => {
    if (!keepQuery.trim()) return;
    setKeepLoading(true);
    setKeepResults(null);
    try {
      const res = await phappsRequest(
        `${API_PATHS.ZEPHYR_ADMIN_MERGE_CUSTOMER_SEARCH}?search=${encodeURIComponent(keepQuery.trim())}`, "GET"
      );
      setKeepResults(res?.data ?? []);
    } finally {
      setKeepLoading(false);
    }
  }, [keepQuery]);

  const selectKeep = useCallback(async (row) => {
    // Fetch full details (including chemistID)
    const res = await phappsRequest(
      `${API_PATHS.ZEPHYR_ADMIN_MERGE_CUSTOMER_DETAILS}/${row.customerID}`, "GET"
    );
    const details = res?.data;
    if (!details) return;
    setKeepCustomer(details);
    setCandidateFilter(details.lastName ?? '');
    setSelectedMerge(new Set());
    setCandidates(null);
    setStep('select-merge');
    // Auto-search candidates
    if (details.chemistID && details.lastName) {
      searchCandidates(details.lastName, details.chemistID, details.customerID);
    }
  }, []);

  // ── Candidate search ──
  const searchCandidates = useCallback(async (lastName, chemistID, excludeCustomerID) => {
    setCandidatesLoading(true);
    setCandidates(null);
    try {
      const params = new URLSearchParams({
        lastName: lastName.trim(),
        chemistID,
        excludeCustomerID,
      });
      const res = await phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_MERGE_CANDIDATES}?${params}`, "GET");
      setCandidates(res?.data ?? []);
    } finally {
      setCandidatesLoading(false);
    }
  }, []);

  const handleCandidateSearch = () => {
    if (!keepCustomer?.chemistID || !candidateFilter.trim()) return;
    searchCandidates(candidateFilter, keepCustomer.chemistID, keepCustomer.customerID);
  };

  const toggleMerge = (customerID) => {
    setSelectedMerge(prev => {
      const next = new Set(prev);
      if (next.has(customerID)) next.delete(customerID); else next.add(customerID);
      return next;
    });
  };

  const selectedCandidates = (candidates ?? []).filter(c => selectedMerge.has(c.customerID));

  // ── Execute merge ──
  const handleMerge = async () => {
    setMerging(true);
    try {
      const res = await phappsRequest(API_PATHS.ZEPHYR_ADMIN_MERGE_CUSTOMERS, "POST", {
        keepCustomerID: keepCustomer.customerID,
        mergeCustomerIDs: [...selectedMerge],
      });
      setMergeResult(res?.data);
      setStep('done');
    } finally {
      setMerging(false);
    }
  };

  const goToCustomer = () => {
    navigate('/zephyr-admin/customers', { state: { claimID: keepCustomer.claimID } });
  };

  // ── Render ──
  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      {/* Header bar */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, flexShrink: 0 }}>
        <Tooltip title="Back to Tools">
          <IconButton size="small" onClick={onDone} sx={{ color: Z_PURPLE }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography fontWeight={700} sx={{ color: Z_PURPLE }}>Merge Patient Records</Typography>
        {step !== 'search-keep' && step !== 'done' && (
          <Chip
            label={step === 'select-merge' ? 'Step 2: Select to Merge' : 'Step 3: Confirm'}
            size="small"
            sx={{ background: `${Z_PURPLE}18`, color: Z_PURPLE, fontWeight: 600 }}
          />
        )}
      </Box>

      {/* ── Step 1: Search for KEEP customer ── */}
      {step === 'search-keep' && (
        <Box sx={{ maxWidth: 860 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Search for the patient record to <strong>keep</strong>. All scripts from duplicate records will be moved into this one.
          </Alert>
          <Box sx={{ display: "flex", gap: 1.5, mb: 2, alignItems: "center" }}>
            <TextField
              label="Last name  (or  last name  first name)"
              size="small"
              value={keepQuery}
              onChange={e => setKeepQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && searchKeep()}
              sx={{ width: 320 }}
              autoFocus
            />
            <Button variant="contained" startIcon={<SearchIcon />}
              onClick={searchKeep} disabled={keepLoading || !keepQuery.trim()}
              sx={{ textTransform: "none", fontWeight: 700, background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})` }}>
              Search
            </Button>
            {keepLoading && <CircularProgress size={22} sx={{ color: Z_PURPLE }} />}
          </Box>

          {keepResults !== null && (
            <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, background: "#fff" }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: `${Z_PURPLE}08` }}>
                  <TableRow>
                    {["Name", "Claim #", "Insurer / Employer", "Scripts", "Last Dispensed", ""].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 1 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {keepResults.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No results.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : keepResults.map(row => (
                    <CustomerResultRow
                      key={row.customerID}
                      row={row}
                      actionLabel="Select as Keep"
                      actionColor="primary"
                      onAction={selectKeep}
                      onViewScripts={r => setScriptReport({ claimID: r.claimID, fullName: r.fullName })}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* ── Step 2: Show keep details + select merge candidates ── */}
      {step === 'select-merge' && keepCustomer && (
        <Box sx={{ display: "flex", gap: 2.5, flex: 1, minHeight: 0, overflow: "hidden" }}>

          {/* Left: Keep customer details */}
          <Box sx={{ width: 280, flexShrink: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Paper variant="outlined" sx={{
              borderRadius: 2, p: 2, borderColor: "#10b981", borderWidth: 2,
              background: "#f0fdf4",
            }}>
              <Typography variant="caption" fontWeight={700} sx={{ color: "#059669", display: "block", mb: 1 }}>
                KEEPING THIS RECORD
              </Typography>
              <Typography fontWeight={700} sx={{ color: Z_PURPLE, mb: 0.5 }}>{keepCustomer.fullName}</Typography>
              <Stack spacing={0.4}>
                <Typography variant="caption" color="text.secondary">
                  Customer #{keepCustomer.customerID} · Claim #{keepCustomer.claimID}
                </Typography>
                {keepCustomer.claimNumber && (
                  <Typography variant="caption" color="text.secondary">Claim #: {keepCustomer.claimNumber}</Typography>
                )}
                {(keepCustomer.insCompName || keepCustomer.employerName) && (
                  <Typography variant="caption" color="text.secondary">
                    {keepCustomer.insCompName ?? keepCustomer.employerName}
                  </Typography>
                )}
                {keepCustomer.customerAddress && (
                  <Typography variant="caption" color="text.secondary">
                    {[keepCustomer.customerAddress, keepCustomer.customerSuburb, keepCustomer.customerPostcode].filter(Boolean).join(', ')}
                  </Typography>
                )}
                {keepCustomer.chemistID && (
                  <Typography variant="caption" color="text.secondary">Chemist: {keepCustomer.chemistID}</Typography>
                )}
              </Stack>
              <Box sx={{ mt: 1.5, display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                <Button size="small" variant="outlined"
                  startIcon={<ListAltIcon fontSize="small" />}
                  onClick={() => setScriptReport({ claimID: keepCustomer.claimID, fullName: keepCustomer.fullName })}
                  sx={{ textTransform: "none", fontSize: "0.72rem", borderColor: Z_PURPLE, color: Z_PURPLE, py: 0.25 }}>
                  Scripts
                </Button>
                <Button size="small" variant="outlined" color="inherit"
                  onClick={() => { setStep('search-keep'); setKeepCustomer(null); setKeepResults(null); }}
                  sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.25 }}>
                  Change
                </Button>
              </Box>
            </Paper>

            {/* Proceed button */}
            {selectedMerge.size > 0 && (
              <Button variant="contained" color="error"
                onClick={() => setStep('confirming')}
                sx={{ textTransform: "none", fontWeight: 700, background: "#dc2626" }}>
                Proceed ({selectedMerge.size} to merge)
              </Button>
            )}
          </Box>

          {/* Right: Merge candidates */}
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1.5, overflow: "hidden" }}>
            <Typography variant="body2" fontWeight={700} sx={{ color: Z_PURPLE }}>
              Select Duplicate Records to Merge
            </Typography>
            <Alert severity="warning" sx={{ py: 0.5 }}>
              Only records at the <strong>same chemist</strong> are shown. Scripts from selected records will be moved to the keep record and the duplicate records will be deactivated.
            </Alert>

            {/* Filter row */}
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              <TextField
                label="Last name filter"
                size="small"
                value={candidateFilter}
                onChange={e => setCandidateFilter(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCandidateSearch()}
                sx={{ width: 220 }}
              />
              <Button variant="outlined" startIcon={<SearchIcon />}
                onClick={handleCandidateSearch}
                disabled={candidatesLoading || !candidateFilter.trim()}
                sx={{ textTransform: "none", borderColor: Z_PURPLE, color: Z_PURPLE }}>
                Search
              </Button>
              {candidatesLoading && <CircularProgress size={20} sx={{ color: Z_PURPLE }} />}
            </Box>

            {/* Candidates table */}
            {candidates !== null && (
              <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, background: "#fff", flex: 1, overflowY: "auto" }}>
                <Table size="small" stickyHeader>
                  <TableHead sx={{ backgroundColor: `${Z_PURPLE}08` }}>
                    <TableRow>
                      <TableCell sx={{ width: "1px", py: 1, background: `${Z_PURPLE}08` }} />
                      {["Name", "Claim #", "Insurer / Employer", "Scripts", "Last Dispensed", ""].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 1, background: `${Z_PURPLE}08` }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {candidates.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
                            No other patients with that last name at this chemist.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : candidates.map(c => (
                      <TableRow key={c.customerID}
                        sx={{
                          "& td": { py: 0.75, px: 1.5, fontSize: "0.82rem" },
                          background: selectedMerge.has(c.customerID) ? "#fff7ed" : "transparent",
                        }}>
                        <TableCell>
                          <Checkbox
                            checked={selectedMerge.has(c.customerID)}
                            onChange={() => toggleMerge(c.customerID)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: selectedMerge.has(c.customerID) ? "#dc2626" : "inherit" }}>
                          {c.fullName}
                        </TableCell>
                        <TableCell>{c.claimNumber ?? "—"}</TableCell>
                        <TableCell sx={{ color: "#64748b", fontSize: "0.78rem" }}>
                          {c.insCompName ?? c.employerName ?? "—"}
                        </TableCell>
                        <TableCell align="center">{c.totalScripts}</TableCell>
                        <TableCell>{fmtDate(c.lastDispensed)}</TableCell>
                        <TableCell>
                          <Tooltip title="View full script list">
                            <IconButton size="small"
                              onClick={() => setScriptReport({ claimID: c.claimID, fullName: c.fullName })}
                              sx={{ color: "#64748b", p: 0.5 }}>
                              <ListAltIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      )}

      {/* ── Step 4: Done ── */}
      {step === 'done' && (
        <Box sx={{ maxWidth: 540 }}>
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 3, borderColor: "#10b981", borderWidth: 2, background: "#f0fdf4" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <CheckCircleOutlineIcon sx={{ color: "#059669", fontSize: 28 }} />
              <Typography fontWeight={700} sx={{ color: "#059669", fontSize: "1.05rem" }}>Merge Complete</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {mergeResult?.mergedCustomerIDs?.length ?? 0} record(s) merged into{" "}
              <strong>{keepCustomer?.fullName}</strong> (Customer #{keepCustomer?.customerID}).
              All scripts have been moved and duplicate records deactivated. A comment has been logged on each record.
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button variant="contained"
                startIcon={<PersonSearchIcon />}
                onClick={goToCustomer}
                sx={{ textTransform: "none", fontWeight: 700, background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})` }}>
                Go to Patient
              </Button>
              <Button variant="outlined" onClick={onDone}
                sx={{ textTransform: "none", borderColor: Z_PURPLE, color: Z_PURPLE }}>
                Back to Tools
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* ── Confirmation Dialog ── */}
      <Dialog open={step === 'confirming'} onClose={() => !merging && setStep('select-merge')} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <DialogTitle sx={{
          background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5,
        }}>
          <span>Confirm Patient Merge</span>
          <IconButton onClick={() => setStep('select-merge')} size="small" sx={{ color: "#fff" }} disabled={merging}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "20px !important" }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action <strong>cannot be undone</strong>. The following records will be permanently deactivated.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            <strong>Keeping:</strong>{" "}
            <span style={{ color: Z_PURPLE, fontWeight: 700 }}>{keepCustomer?.fullName}</span>{" "}
            (Customer #{keepCustomer?.customerID})
          </Typography>
          <Typography variant="body2" fontWeight={700} sx={{ mb: 0.75 }}>Merging (will be deactivated):</Typography>
          <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1, mb: 2 }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: `${Z_PURPLE}08` }}>
                <TableRow>
                  {["Name", "Claim #", "Scripts"].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 0.75 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedCandidates.map(c => (
                  <TableRow key={c.customerID} sx={{ "& td": { py: 0.5, fontSize: "0.8rem" } }}>
                    <TableCell sx={{ color: "#dc2626", fontWeight: 600 }}>{c.fullName}</TableCell>
                    <TableCell>{c.claimNumber ?? "—"}</TableCell>
                    <TableCell align="center">{c.totalScripts}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary">
            All scripts from the above records will be moved to {keepCustomer?.fullName}. A comment will be logged on every affected record.
            PH Common customer records will be relinked to the kept customer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStep('select-merge')} sx={{ textTransform: "none" }} disabled={merging}>
            Back
          </Button>
          <Button variant="contained" color="error" onClick={handleMerge} disabled={merging}
            startIcon={merging ? <CircularProgress size={16} color="inherit" /> : <MergeIcon />}
            sx={{ textTransform: "none", fontWeight: 700 }}>
            {merging ? "Merging…" : "Confirm Merge"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Script report dialog */}
      <ScriptReportDialog
        open={!!scriptReport}
        claimID={scriptReport?.claimID}
        fullName={scriptReport?.fullName}
        onClose={() => setScriptReport(null)}
      />
    </Box>
  );
}

// ── Tools Landing ─────────────────────────────────────────────────────────────
const TOOLS = [
  {
    id: 'merge-customer',
    label: 'Merge Patient Records',
    description: 'Consolidate duplicate patient entries from the same chemist. Moves all scripts to the kept record and deactivates the duplicates.',
    Icon: MergeIcon,
    color: "#7c3aed",
  },
];

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ZephyrToolsPage() {
  const [activeTool, setActiveTool] = useState(null);
  const [toast, setToast] = useState(null);

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2, height: "100%", minHeight: 0, overflow: "hidden" }}>

      {/* Page header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: 1.5,
          background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <img src={ZephyrIconWhite} alt="" style={{ height: 20, objectFit: "contain" }} />
        </Box>
        <Typography fontWeight={800} fontSize="1.25rem" sx={{ color: Z_PURPLE }}>Tools</Typography>
      </Box>

      {/* Tool cards (landing) */}
      {!activeTool && (
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {TOOLS.map(tool => (
            <Paper
              key={tool.id}
              variant="outlined"
              onClick={() => setActiveTool(tool.id)}
              sx={{
                p: 2.5, width: 260, borderRadius: 2, cursor: "pointer",
                borderColor: "#e2e8f0", transition: "all 0.15s",
                "&:hover": { borderColor: tool.color, boxShadow: `0 0 0 2px ${tool.color}22`, background: `${tool.color}06` },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: 1.5, background: `${tool.color}18`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <tool.Icon sx={{ fontSize: 20, color: tool.color }} />
                </Box>
                <Typography fontWeight={700} sx={{ color: tool.color }}>{tool.label}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                {tool.description}
              </Typography>
            </Paper>
          ))}
        </Box>
      )}

      {/* Active tool */}
      {activeTool === 'merge-customer' && (
        <MergeCustomerTool onDone={() => setActiveTool(null)} />
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast?.severity ?? "success"} onClose={() => setToast(null)} sx={{ width: "100%" }}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
